import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, map, Observable, of, switchMap } from 'rxjs';
import * as Papa from 'papaparse';
import { AuthService } from '../auth/auth.service';
import { IHistoryDeleteBatchResponse } from '../models/IHistoryDeleteBatch';
import { IQuerySubmission, QueryResultRow } from '../models/IQuerySubmission';
import { environment } from '../../environments/environment';
import { IHistoryPageResponse } from '../models/IHistoryResult';
import { QueryPlanResponse } from '../models/IQueryPlan';

@Injectable({ providedIn: 'root' })
export class SqlQueryService {
  private readonly apiBase = `${environment.API_BASE_URL}/api`;
  private readonly historyBase = `${environment.API_BASE_URL}/api/history`;

  constructor(
    private http: HttpClient,
    private auth: AuthService,
  ) {}

  submitQuery(query: string): Observable<IQuerySubmission> {
    return this.http
      .post<unknown>(
        `${this.apiBase}/query`,
        { query },
        { headers: this.authHeaders(true) }, // prefer ID token for user identity/email
      )
      .pipe(map((response) => this.normalizeJobLookupResponse(response, '', { query })));
  }

  planQuery(query: string): Observable<QueryPlanResponse> {
    return this.http.post<QueryPlanResponse>(
      `${this.apiBase}/query/plan`,
      { query },
      { headers: this.authHeaders(true) },
    );
  }

  fetchSubmissionMeta(querySubmission: IQuerySubmission): Observable<IQuerySubmission> {
    const jobId = (querySubmission.job_id || '').trim();
    if (!jobId) return of(querySubmission);
    return this.fetchQueryByJobId(jobId, querySubmission);
  }

  fetchQueryByJobId(
    jobId: string,
    fallbackSubmission?: Partial<IQuerySubmission> | null,
  ): Observable<IQuerySubmission> {
    const encodedJobId = encodeURIComponent(jobId);
    return this.http
      .get<unknown>(`${this.apiBase}/query/${encodedJobId}`, {
        headers: this.authHeaders(true),
      })
      .pipe(
        map((response) => this.normalizeJobLookupResponse(response, jobId, fallbackSubmission)),
      );
  }

  fetchCsvResults(
    resultUrl: string,
    expectedRowCount?: number | null,
    resultKey?: string | null,
    resultContentType?: string | null,
  ): Observable<QueryResultRow[]> {
    if (this.isZipResult(resultUrl, resultKey, resultContentType)) {
      return this.fetchZipCsvText(resultUrl).pipe(
        map((csvText) => this.parseCsvRows(csvText, expectedRowCount)),
      );
    }

    return this.fetchCsvText(resultUrl).pipe(
      map((csvText) => this.parseCsvRows(csvText, expectedRowCount)),
    );
  }

  fetchCsvText(resultUrl: string): Observable<string> {
    return this.http.get(resultUrl, { responseType: 'text' });
  }

  fetchZipCsvText(resultUrl: string): Observable<string> {
    return this.http.get(resultUrl, { responseType: 'arraybuffer' }).pipe(
      switchMap((archiveBytes) => from(this.extractCsvTextFromZip(archiveBytes))),
    );
  }

  getUserHistoryPage(
    uid: string,
    limit = 25,
    cursor: string | null = null,
  ): Observable<IHistoryPageResponse> {
    let params = new HttpParams().set('limit', String(limit));
    if (cursor) {
      params = params.set('cursor', cursor);
    }
    return this.http.get<IHistoryPageResponse>(`${this.historyBase}/${this.safeUser(uid)}`, {
      headers: this.authHeaders(true),
      params,
    });
  }

  buildHistoryObjectUrl(key: string): string {
    const encodedKey = this.encodeS3KeyPreservingSlashes(key);
    return `https://${environment.AWS_S3_BUCKET}.s3.amazonaws.com/${encodedKey}`;
  }

  deleteHistoryBatch(uid: string, filenames: string[]): Observable<IHistoryDeleteBatchResponse> {
    return this.http.post<IHistoryDeleteBatchResponse>(
      `${this.historyBase}/${this.safeUser(uid)}/delete`,
      { filenames },
      { headers: this.authHeaders(true) },
    );
  }

  private authHeaders(preferIdToken = false): HttpHeaders {
    const tokens = this.auth.tokens();
    const token = preferIdToken ? tokens?.idToken : tokens?.accessToken;
    if (!token) return new HttpHeaders();
    return new HttpHeaders({ Authorization: `Bearer ${token}` });
  }

  private normalizeJobLookupResponse(
    payload: unknown,
    fallbackJobId: string,
    fallbackSubmission?: Partial<IQuerySubmission> | null,
  ): IQuerySubmission {
    const root = this.asRecord(payload);
    const topLevelData = this.asRecord(root?.['data']);
    const submissionRecord =
      this.findSubmissionRecord(root) ??
      this.findSubmissionRecord(topLevelData) ??
      this.findSubmissionRecord(this.asRecord(topLevelData?.['submission']));
    const resolved = submissionRecord ?? {};
    const authEmail = this.auth.user()?.email || '';

    return this.mergeSubmissionHints(
      {
        job_id: this.toStringValue(resolved['job_id']) || fallbackJobId,
        query: this.toStringValue(resolved['query']) || '',
        status: this.coerceStatus(this.toStringValue(resolved['status'])?.toLowerCase()),
        username:
          this.toStringValue(resolved['username']) ||
          this.toStringValue(resolved['uid']) ||
          authEmail,
        uid: this.toNullableString(resolved['uid']) ?? undefined,
        creation_time: this.toStringValue(resolved['creation_time']) || '',
        processing_start_time: this.toNullableString(resolved['processing_start_time']),
        queued_duration_seconds: this.toNullableNumber(resolved['queued_duration_seconds']),
        processing_duration_seconds: this.toNullableNumber(
          resolved['processing_duration_seconds'],
        ),
        query_s3_key: this.toNullableString(resolved['query_s3_key']),
        result_s3_key: this.toNullableString(resolved['result_s3_key']),
        result_url: this.toNullableString(resolved['result_url']),
        result_content_type: this.toNullableString(resolved['result_content_type']),
        result_content_encoding: this.toNullableString(resolved['result_content_encoding']),
        result_row_count: this.toNullableNumber(resolved['result_row_count']),
        error_message: this.toNullableString(resolved['error_message']),
        result_message: this.toNullableString(resolved['result_message']),
      },
      fallbackSubmission,
    );
  }

  private findSubmissionRecord(
    value: Record<string, unknown> | null,
  ): Record<string, unknown> | null {
    if (!value) return null;
    if (this.looksLikeSubmission(value)) return value;
    const nestedCandidates = [
      this.asRecord(value['submission']),
      this.asRecord(value['query']),
      this.asRecord(value['job']),
      this.asRecord(value['meta']),
    ];
    for (const candidate of nestedCandidates) {
      if (candidate && this.looksLikeSubmission(candidate)) return candidate;
    }
    return null;
  }

  private looksLikeSubmission(value: Record<string, unknown>): boolean {
    return (
      typeof value['job_id'] === 'string' ||
      typeof value['status'] === 'string' ||
      typeof value['query'] === 'string' ||
      typeof value['creation_time'] === 'string' ||
      typeof value['result_url'] === 'string' ||
      typeof value['result_s3_key'] === 'string' ||
      typeof value['query_s3_key'] === 'string'
    );
  }

  private parseCsvRows(csvText: string, expectedRowCount?: number | null): QueryResultRow[] {
    const parsed = Papa.parse<Record<string, unknown>>(csvText, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.replace(/^\uFEFF/, '').trim(),
    });

    const fatalErrors = parsed.errors.filter((error) => error.code !== 'UndetectableDelimiter');
    if (fatalErrors.length > 0) {
      const firstError = fatalErrors[0];
      throw new Error(firstError?.message || 'Failed to parse CSV result file.');
    }

    const positiveExpectedRowCount =
      typeof expectedRowCount === 'number' && expectedRowCount > 0 ? expectedRowCount : null;
    if (positiveExpectedRowCount && parsed.data.length !== positiveExpectedRowCount) {
      const headerlessRows = this.parseHeaderlessCsvRows(csvText);
      if (headerlessRows.length === positiveExpectedRowCount) return headerlessRows;
    }

    return parsed.data;
  }

  private async extractCsvTextFromZip(archiveBytes: ArrayBuffer): Promise<string> {
    const bytes = new Uint8Array(archiveBytes);
    const view = new DataView(archiveBytes);
    const eocdOffset = this.findZipEndOfCentralDirectory(view);
    const entryCount = view.getUint16(eocdOffset + 10, true);
    const centralDirectoryOffset = view.getUint32(eocdOffset + 16, true);
    const entries = this.readZipCentralDirectory(view, bytes, centralDirectoryOffset, entryCount);
    const entry = entries.find((item) => /\.csv$/i.test(item.filename)) ?? entries[0];

    if (!entry) {
      throw new Error('The ZIP result did not contain a CSV file.');
    }

    const compressedBytes = this.readZipEntryBytes(view, bytes, entry);
    if (entry.compressionMethod === 0) {
      return new TextDecoder().decode(compressedBytes);
    }

    if (entry.compressionMethod !== 8) {
      throw new Error('The ZIP result uses an unsupported compression method.');
    }

    if (typeof DecompressionStream === 'undefined') {
      throw new Error('This browser cannot preview compressed ZIP results.');
    }

    const compressedBuffer = compressedBytes.buffer.slice(
      compressedBytes.byteOffset,
      compressedBytes.byteOffset + compressedBytes.byteLength,
    ) as ArrayBuffer;
    const stream = new Blob([compressedBuffer])
      .stream()
      .pipeThrough(new DecompressionStream('deflate-raw'));
    const decompressedBytes = await new Response(stream).arrayBuffer();
    return new TextDecoder().decode(decompressedBytes);
  }

  private findZipEndOfCentralDirectory(view: DataView): number {
    const eocdSignature = 0x06054b50;
    const minimumOffset = Math.max(0, view.byteLength - 65_557);
    for (let offset = view.byteLength - 22; offset >= minimumOffset; offset--) {
      if (view.getUint32(offset, true) === eocdSignature) return offset;
    }

    throw new Error('The ZIP result could not be read.');
  }

  private readZipCentralDirectory(
    view: DataView,
    bytes: Uint8Array,
    offset: number,
    entryCount: number,
  ): Array<{
    filename: string;
    compressionMethod: number;
    compressedSize: number;
    localHeaderOffset: number;
  }> {
    const centralDirectorySignature = 0x02014b50;
    const decoder = new TextDecoder();
    const entries: Array<{
      filename: string;
      compressionMethod: number;
      compressedSize: number;
      localHeaderOffset: number;
    }> = [];
    let cursor = offset;

    for (let index = 0; index < entryCount; index++) {
      if (view.getUint32(cursor, true) !== centralDirectorySignature) break;

      const compressionMethod = view.getUint16(cursor + 10, true);
      const compressedSize = view.getUint32(cursor + 20, true);
      const filenameLength = view.getUint16(cursor + 28, true);
      const extraLength = view.getUint16(cursor + 30, true);
      const commentLength = view.getUint16(cursor + 32, true);
      const localHeaderOffset = view.getUint32(cursor + 42, true);
      const filenameStart = cursor + 46;
      const filename = decoder.decode(bytes.slice(filenameStart, filenameStart + filenameLength));

      if (filename && !filename.endsWith('/')) {
        entries.push({ filename, compressionMethod, compressedSize, localHeaderOffset });
      }

      cursor = filenameStart + filenameLength + extraLength + commentLength;
    }

    return entries;
  }

  private readZipEntryBytes(
    view: DataView,
    bytes: Uint8Array,
    entry: { compressedSize: number; localHeaderOffset: number },
  ): Uint8Array {
    const localFileSignature = 0x04034b50;
    const offset = entry.localHeaderOffset;
    if (view.getUint32(offset, true) !== localFileSignature) {
      throw new Error('The ZIP result could not be read.');
    }

    const filenameLength = view.getUint16(offset + 26, true);
    const extraLength = view.getUint16(offset + 28, true);
    const dataStart = offset + 30 + filenameLength + extraLength;
    return bytes.slice(dataStart, dataStart + entry.compressedSize);
  }

  private isZipResult(
    resultUrl: string,
    resultKey?: string | null,
    resultContentType?: string | null,
  ): boolean {
    const normalizedContentType = (resultContentType || '').toLowerCase();
    if (
      normalizedContentType.includes('application/zip') ||
      normalizedContentType.includes('application/x-zip-compressed')
    ) {
      return true;
    }

    const keyOrUrl = `${resultKey || ''} ${resultUrl || ''}`.toLowerCase();
    return /\.zip(?:$|[?#\s])/.test(keyOrUrl);
  }

  private parseHeaderlessCsvRows(csvText: string): QueryResultRow[] {
    const parsed = Papa.parse<unknown[]>(csvText, {
      header: false,
      skipEmptyLines: true,
    });

    const fatalErrors = parsed.errors.filter((error) => error.code !== 'UndetectableDelimiter');
    if (fatalErrors.length > 0) {
      const firstError = fatalErrors[0];
      throw new Error(firstError?.message || 'Failed to parse CSV result file.');
    }

    return parsed.data.map((row) => {
      const values = Array.isArray(row) ? row : [row];
      return values.reduce<QueryResultRow>((acc, value, index) => {
        acc[`column_${index + 1}`] = value;
        return acc;
      }, {});
    });
  }

  private asRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null;
  }

  private toStringValue(value: unknown): string | null {
    return typeof value === 'string' ? value : null;
  }

  private toNullableString(value: unknown): string | null {
    return typeof value === 'string' && value ? value : null;
  }

  private toNullableNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  }

  private coerceStatus(status: string | undefined): IQuerySubmission['status'] {
    switch (status) {
      case 'pending':
      case 'queued':
      case 'running':
      case 'processing':
      case 'success':
      case 'partial':
      case 'error':
        return status;
      default:
        return 'error';
    }
  }

  private mergeSubmissionHints(
    submission: IQuerySubmission,
    hint?: Partial<IQuerySubmission> | null,
  ): IQuerySubmission {
    if (!hint) return submission;
    return {
      ...submission,
      job_id: submission.job_id || this.stringOrEmpty(hint.job_id),
      query: submission.query || this.stringOrEmpty(hint.query),
      username: submission.username || this.stringOrEmpty(hint.username),
      uid: submission.uid || this.nullableStringFromHint(hint.uid) || undefined,
      creation_time: submission.creation_time || this.stringOrEmpty(hint.creation_time),
      processing_start_time:
        submission.processing_start_time ||
        this.nullableStringFromHint(hint.processing_start_time),
      queued_duration_seconds:
        submission.queued_duration_seconds ??
        this.nullableNumberFromHint(hint.queued_duration_seconds),
      processing_duration_seconds:
        submission.processing_duration_seconds ??
        this.nullableNumberFromHint(hint.processing_duration_seconds),
      query_s3_key: submission.query_s3_key || this.nullableStringFromHint(hint.query_s3_key),
      result_s3_key: submission.result_s3_key || this.nullableStringFromHint(hint.result_s3_key),
      result_url: submission.result_url || this.nullableStringFromHint(hint.result_url),
      result_content_type:
        submission.result_content_type || this.nullableStringFromHint(hint.result_content_type),
      result_content_encoding:
        submission.result_content_encoding ||
        this.nullableStringFromHint(hint.result_content_encoding),
      result_row_count:
        submission.result_row_count ?? this.nullableNumberFromHint(hint.result_row_count),
      error_message: submission.error_message || this.nullableStringFromHint(hint.error_message),
      result_message: submission.result_message || this.nullableStringFromHint(hint.result_message),
    };
  }

  private stringOrEmpty(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private nullableStringFromHint(value: unknown): string | null {
    return typeof value === 'string' && value ? value : null;
  }

  private nullableNumberFromHint(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }

  private safeUser(input: string): string {
    return (input || 'user').toLowerCase().replace(/[@.]/g, '-');
  }

  private encodeS3KeyPreservingSlashes(key: string): string {
    return (key || '')
      .split('/')
      .map((segment) => encodeURIComponent(segment))
      .join('/');
  }
}
